import { scaleUtc, scaleLinear } from '@vx/scale'
import { extent, bisector } from 'd3-array'
import Color from 'color'

class LineChartService {
  /**
   * Iterate over the series and pull out all the distinct x and y values
   * Supports numbers or dates
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {Object} options
   * @param {boolean} options.includeBands Whether to include band range values when building a distinct list of values
   * @param {Object} options.seriesToHide Whether a particular series is hidden or not
   * @returns {Object} Object with an x and y array of distinct values
   */
  getDistinctValues (series = [], { includeBands = false, seriesToHide = {} } = {}) {
    let isXDate = false
    const distinctValues = series.reduce((distinctValues, s) => {
      if (this.isSeriesHidden(s, seriesToHide)) {
        return distinctValues
      }

      (s.points || []).forEach(({ x, y }) => {
        if (x instanceof Date) {
          isXDate = true
          x = x.getTime()
        }
        distinctValues.x.add(x)
        distinctValues.y.add(y)
      })
      if (includeBands && s.bands && s.bands.length > 0) {
        s.bands.forEach(({ points }) => {
          points.forEach(({ y0, y1 }) => {
            distinctValues.y.add(y0)
            distinctValues.y.add(y1)
          })
        })
      }
      return distinctValues
    }, { x: new Set(), y: new Set() })
    let xValues = [ ...distinctValues.x ]
    if (isXDate) {
      xValues = xValues.map((x) => new Date(x))
    }
    return { x: xValues, y: Array.from(distinctValues.y) }
  }

  /**
   * Calculates the D3 scale for a list of x and y values given a dimension and whether x is time series
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {Object} dimensions Object with a width and height number of pixels
   * @param {boolean} isTimeSeries Whether x is a time series or not
   * @param {object} configDomains Array with min and max x values
   * @param {number[]} domains.x Array with min and max x values
   * @param {number[]} domains.y Array with min and max y values
   * @param {Object} seriesToHide Whether a particular series is hidden or not
   */
  calculateScales (series, dimensions, isTimeSeries, configDomains = {}, seriesToHide) {
    const { width, height } = dimensions
    const scales = {}
    const { x: xDomain, y: yDomain } = this.getDomains(configDomains, series, seriesToHide)

    if (isTimeSeries) {
      scales.x = scaleUtc({
        range: [0, width],
        domain: xDomain
      })
    } else {
      scales.x = scaleLinear({
        range: [0, width],
        domain: xDomain
      })
    }

    scales.y = scaleLinear({
      range: [height, 0],
      domain: yDomain
    })

    return scales
  }

  getDomains (configDomains = {}, series, seriesToHide = {}) {
    // If we've already got a full domain for box axis, return it
    if (configDomains.x && configDomains.x.length > 1 && configDomains.y && configDomains.y.length > 1) {
      return configDomains
    }

    // Get the distinct values including the band range values
    const { x: xValues, y: yValues } = this.getDistinctValues(series, { includeBands: true, seriesToHide })
    const xExtent = extent(xValues)
    const yExtent = extent(yValues)

    const result = {
      x: configDomains.x || xExtent,
      y: configDomains.y || yExtent
    }

    if (result.x.length === 1) {
      result.x = [ ...result.x, xExtent[1] ]
    }
    if (result.y.length === 1) {
      result.y = [ ...result.y, yExtent[1] ]
    }

    return result
  }

  /**
   * Find the closest point in each series to the given x value
   * If there is a point missing on any of the series, it will provide the previous & next points
   *
   * @param {Object[]} series Array of series objects with a points array property
   * @param {*} x X value to find the closest point to
   */
  findClosestPoints (series, x) {
    const closestIndexes = series.map(({ points, name }) => ({ points, name, closestIndex: this.findClosestPointIndex(points, x) }))

    // First find the closest X point across all series
    let currentX = closestIndexes.reduce((currentX, { points, closestIndex }) => {
      const closestXForSeries = points[closestIndex].x
      return (Math.abs(x - closestXForSeries) < Math.abs(x - currentX))
        ? closestXForSeries
        : currentX
    }, Infinity)

    // Then map the point where x == currentX
    // If there is no x at currentX, map prev & next points
    return closestIndexes.map(({ points, name, closestIndex }) => {
      currentX = (currentX instanceof Date) ? currentX.getTime() : currentX
      const closestPointX = (points[closestIndex].x instanceof Date)
        ? points[closestIndex].x.getTime()
        : points[closestIndex].x

      if (closestPointX === currentX) {
        return { current: points[closestIndex], name }
      } else if (closestPointX < currentX) {
        return {
          previous: points[closestIndex],
          next: (closestIndex < points.length - 1) ? points[closestIndex + 1] : undefined,
          name
        }
      } else {
        return {
          previous: (closestIndex > 0) ? points[closestIndex - 1] : undefined,
          next: points[closestIndex],
          name
        }
      }
    })
  }

  /**
   * Find the closest point in a series of points given an x value
   *
   * @param {Object[]} points Array of point objects with x property
   * @param {*} x X value to find the closest point to
   */
  findClosestPointIndex (points, x) {
    const bisect = bisector((p) => p.x).right
    let closestPointIndex = bisect(points, x, 1)

    if (closestPointIndex >= points.length) {
      // x is to the right of the right-most point - use right-most index
      return points.length - 1
    }

    if (closestPointIndex > 0) {
      // check if x is closer to the point on the left or the right
      const left = points[closestPointIndex - 1].x
      const right = points[closestPointIndex].x
      if ((x - left) < (right - x)) {
        closestPointIndex = closestPointIndex - 1
      }
    }
    return closestPointIndex
  }

  /**
   * Tranform the yLo, yHi, yLo2, yHi2, etc form of bands into an array of bands with names and colors
   *
   * @param {object[]} points Array of points with y, yLo, yHi, yLo2, yHi2, etc values
   * @param {string[]} bandNames Array of band names that correspond to the lo/hi pairs
   * @param {string} lineColor Color of the line the band corresponds to
   */
  getBands (points, bandNames, lineColor) {
    const bands = []
    const getPointValue = (point, propName, bandNum) => point[`${propName}${bandNum > 1 ? bandNum : ''}`]

    const bandCount = points.reduce((bandCount, point) => {
      while (true) {
        const propNames = ['yLo', 'yHi']
        // If there is a lo or hi value for the next band, increment and keep looking
        if (propNames.find((propName) => getPointValue(point, propName, bandCount + 1) !== undefined)) {
          bandCount++
        } else {
          break
        }
      }
      return bandCount
    }, 0)

    const bandColors = this.getBandColors(lineColor, bandCount)

    for (let i = 1; i <= bandCount; i++) {
      bands.push({
        name: (bandNames && bandNames.length > (i - 1)) ? bandNames[i - 1] : `Band ${i}`,
        color: bandColors[i - 1],
        points: points.map((point) => {
          const y0 = getPointValue(point, 'yLo', i)
          const y1 = getPointValue(point, 'yHi', i)
          return {
            x: point.x,
            y0: (y0 !== undefined) ? y0 : point.y,
            y1: (y1 !== undefined) ? y1 : point.y
          }
        })
      })
    }

    return bands
  }

  /**
   * Get a list of band colors ranging from light to dark within an acceptable range
   */
  getBandColors (lineColor, count) {
    const bandColors = []
    if (!count) {
      return bandColors
    }
    const lightest = 0.8
    const darkest = 0.5

    // if more than one band, divide up the acceptable range into even chunks
    const increment = (count > 1) ? (lightest - darkest) / (count - 1) : 0
    let next = lightest
    for (let i = 0; i < count; i++) {
      bandColors.push(Color(lineColor).fade(next))
      next -= increment
    }
    return bandColors
  }

  isSeriesHidden (series, seriesToHide) {
    return seriesToHide[series.name] || seriesToHide[series.label]
  }
}

export const lineChartService = new LineChartService()
